/**
 * AllBaise Top Navigation — fixed top bar with logo + nav items + user menu + alert bell.
 * Shows on ALL pages. AllBaise logo always visible.
 */
import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Home, Briefcase, MessageSquare, LayoutDashboard, Users,
  Building2, Gift, GraduationCap, Star, Bell, CheckCheck,
  Loader2, X, Settings, LogOut, Shield, User, AlertTriangle, Mail,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import AllBaiseLogo from "@/components/AllBaiseLogo";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import LanguageSelector from "@/components/common/LanguageSelector";
import { useAuth } from "@/App";
import { useHubAccess } from "@/services/hubAccess";
import { toast } from "sonner";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TOP_ITEMS = [
  { to: "/", label: "Home", icon: Home },
  { to: "/smart-office", label: "Office Tools", icon: Briefcase, requiredHub: "smart_office" },
  { to: "/comms-centre", label: "Comms Centre", icon: MessageSquare, requiredHub: "comms" },
  { to: "/dashboard", label: "Corporate Tools", icon: LayoutDashboard, requiredHub: "dashboard" },
  { to: "/people-directory", label: "My People", icon: Users },
  { to: "/corporate-hub", label: "My Orgs", icon: Building2 },
  { to: "/deals", label: "Deals", icon: Gift },
  { to: "/career", label: "Career Hub", icon: GraduationCap },
  { to: "/wish-centre", label: "Wish Centre", icon: Star },
];

export default function TopNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const hubAccess = useHubAccess();
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotif, setLoadingNotif] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [showAlertRadiusInput, setShowAlertRadiusInput] = useState(false);
  const [alertRadius, setAlertRadius] = useState("");
  const panelRef = useRef(null);

  const isActive = (itemTo) => {
    if (itemTo === "/") return location.pathname === "/";
    return location.pathname.startsWith(itemTo);
  };

  const fetchAlertCount = async () => {
    if (!auth?.user) return;
    try {
      const params = new URLSearchParams();
      if (alertRadius) params.set("radius_km", alertRadius);
      const res = await axios.get(API + "/all-alerts/count?" + params.toString(), { headers: getHeaders() });
      setAlertCount(res.data.count || 0);
    } catch {}
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!auth?.user) return;
    try {
      const res = await axios.get(`${API}/comms/notifications`, { headers: getHeaders() });
      setNotifications(res.data?.notifications || res.data?.items || []);
    } catch {}
  };

  const fetchUnreadCount = async () => {
    if (!auth?.user) return;
    try {
      const res = await axios.get(`${API}/comms/notifications/unread-count`, { headers: getHeaders() });
      setUnreadCount(res.data?.count || 0);
    } catch {}
  };

  useEffect(() => {
    if (auth?.user) {
      fetchUnreadCount();
      fetchAlertCount();
      const notifInterval = setInterval(fetchUnreadCount, 30000);
      const alertInterval = setInterval(fetchAlertCount, 30000);
      return () => { clearInterval(notifInterval); clearInterval(alertInterval); };
    }
  }, [auth?.user, alertRadius]);

  useEffect(() => {
    if (showNotifPanel && auth?.user) {
      setLoadingNotif(true);
      fetchNotifications().finally(() => setLoadingNotif(false));
    }
  }, [showNotifPanel, auth?.user]);

  // Click outside to close notification panel
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowNotifPanel(false);
      }
    };
    if (showNotifPanel) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showNotifPanel]);

  const markAllRead = async () => {
    try {
      for (const n of notifications) {
        if (!n.read) {
          await axios.post(`${API}/comms/notifications/${n.id}/read`, {}, { headers: getHeaders() });
        }
      }
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const markOneRead = async (notifId) => {
    try {
      await axios.post(`${API}/comms/notifications/${notifId}/read`, {}, { headers: getHeaders() });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleLogout = async () => {
    await auth.logout();
    navigate("/");
    toast.success("Logged out");
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <header className="h-12 bg-app-card border-b border-app flex items-center px-3 sticky top-0 z-50">
      {/* Logo — always visible */}
      <Link to="/" className="flex items-center gap-2 mr-3 shrink-0 text-app no-underline">
        <AllBaiseLogo size={22} />
      </Link>

      {/* Desktop Nav Items */}
      <nav className="hidden md:flex items-center gap-0.5 flex-1 overflow-x-auto">
        {TOP_ITEMS.filter(item => !item.requiredHub || hubAccess.hasAccess(item.requiredHub)).map((item) => {
          const active = isActive(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                active
                  ? "bg-blue-600 text-white"
                  : "text-app hover:bg-white/10"
              }`}
            >
              <item.icon className="w-3.5 h-3.5" />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Right side */}
      <div className="flex items-center gap-1 ml-auto">

        {/* Anonymous pricing link */}
        {!auth?.user && (
          <Link to="/pricing" className="text-xs font-medium text-app-secondary hover:text-app px-2 py-1 rounded hover:bg-white/10 transition-colors">
            Pricing
          </Link>
        )}

        {/* 🔴 ALERT BELL — flashing red with incident count & KM filter */}
        {auth?.user && (
          <div className="relative flex items-center gap-1">
            <Link
              to="/all-alerts"
              className="relative p-1.5 rounded-md transition-colors hover:bg-red-600/10 group"
              title="View all alerts"
            >
              <AlertTriangle className={`w-4 h-4 ${alertCount > 0 ? "text-red-500 animate-pulse" : "text-app-secondary"}`} />
              {alertCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-pulse">
                  {alertCount > 99 ? "99+" : alertCount}
                </span>
              )}
            </Link>
            {showAlertRadiusInput ? (
              <div className="flex items-center gap-0.5">
                <input type="number" value={alertRadius} onChange={e => setAlertRadius(e.target.value)}
                  placeholder="km" className="w-12 h-6 text-[9px] bg-surface-item border border-input rounded px-1 text-app" min={1} />
                <button onClick={() => { setShowAlertRadiusInput(false); setAlertRadius(""); }}
                  className="text-[9px] text-app-secondary hover:text-app p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button onClick={() => setShowAlertRadiusInput(true)}
                className="text-[10px] text-app-secondary hover:text-app font-mono p-0.5"
                title="Filter alerts by radius">
                {alertRadius ? `${alertRadius}km` : "km"}
              </button>
            )}
          </div>
        )}

        {/* Mail Icon */}
        {auth?.user && (
          <Link to="/comms-centre?tab=mail" className="p-2 text-app-secondary hover:text-app transition-colors" title="Internal Mail">
            <Mail className="w-4 h-4" />
          </Link>
        )}

        {/* Notification Bell */}
        {auth?.user && (
          <div className="relative" ref={panelRef}>
            <button
              onClick={() => setShowNotifPanel(!showNotifPanel)}
              className="relative p-2 text-app-secondary hover:text-app transition-colors"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Panel */}
            {showNotifPanel && (
              <div className="absolute right-0 top-full mt-1 w-80 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-xl z-50 max-h-[400px] flex flex-col">
                <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 dark:border-zinc-700">
                  <h3 className="text-xs font-semibold text-zinc-800 dark:text-zinc-200">Notifications</h3>
                  <div className="flex items-center gap-1">
                    {unreadCount > 0 && (
                      <button onClick={markAllRead}
                        className="text-[10px] text-blue-500 hover:text-blue-400 flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20">
                        <CheckCheck className="w-3 h-3" /> Mark all read
                      </button>
                    )}
                    <button onClick={() => setShowNotifPanel(false)}
                      className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {loadingNotif ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-zinc-400" /></div>
                  ) : notifications.length === 0 ? (
                    <div className="py-6 text-center">
                      <Bell className="w-6 h-6 text-zinc-300 mx-auto mb-2" />
                      <p className="text-xs text-zinc-400">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.slice(0, 30).map((n) => (
                      <div key={n.id} onClick={() => { if (!n.read) markOneRead(n.id); }}
                        className={`px-3 py-2.5 border-b border-zinc-100 dark:border-zinc-800 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors ${!n.read ? "bg-blue-50/50 dark:bg-blue-900/10" : ""}`}>
                        <div className="flex items-start gap-2">
                          <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                            n.notification_type === "meeting_invite" ? "bg-violet-100 text-violet-600" :
                            n.notification_type === "message" ? "bg-blue-100 text-blue-600" :
                            "bg-zinc-100 text-zinc-600 dark:bg-zinc-700"
                          }`}><Bell className="w-3 h-3" /></div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs ${!n.read ? "font-semibold" : "font-medium"} text-zinc-800 dark:text-zinc-200 truncate`}>{n.title || "Notification"}</p>
                            {n.message && <p className="text-[10px] text-zinc-500 dark:text-zinc-400 line-clamp-2 mt-0.5">{n.message}</p>}
                            <p className="text-[9px] text-zinc-400 mt-1">{formatTime(n.created_at)}</p>
                          </div>
                          {!n.read && <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-1.5" />}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {notifications.length > 0 && (
                  <div className="px-3 py-2 border-t border-zinc-200 dark:border-zinc-700 text-center">
                    <Link to="/comms-centre/notifications" className="text-[10px] text-blue-500 hover:text-blue-400 font-medium">View all notifications</Link>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Language Selector (compact) */}
        {auth?.user && <LanguageSelector compact />}

        {/* User Menu */}
        {auth?.user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 p-1 rounded-md hover:bg-white/5 transition-colors">
                <Avatar className="w-6 h-6">
                  <AvatarImage src={auth.user?.picture} />
                  <AvatarFallback className="bg-violet-600/30 text-violet-300 text-[10px] font-bold">
                    {auth.user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-app-secondary hidden sm:inline max-w-[80px] truncate">{auth.user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 rounded-lg shadow-xl text-zinc-800 dark:text-zinc-200 min-w-[160px]">
              <div className="px-3 py-2 border-b border-zinc-100 dark:border-zinc-800">
                <p className="text-xs font-semibold truncate">{auth.user?.name}</p>
                <p className="text-[10px] text-zinc-500 truncate">{auth.user?.email}</p>
              </div>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/")}>
                <User className="w-4 h-4 mr-2" /> My Profile
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/all-alerts")}>
                <AlertTriangle className="w-4 h-4 mr-2 text-red-500" /> All Alerts
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-200 dark:bg-zinc-700" />
              <div className="px-2 py-2">
                <ThemeSwitcher compact />
              </div>
              <DropdownMenuSeparator className="bg-zinc-200 dark:bg-zinc-700" />
              {auth.user?.role === "admin" && (
                <>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/admin")}>
                    <Shield className="w-4 h-4 mr-2 text-indigo-500" />
                    <span className="text-indigo-500">Admin Panel</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-200 dark:bg-zinc-700" />
                </>
              )}
              <DropdownMenuItem className="cursor-pointer text-red-600" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Mobile: logo centered when no user */}
      {!auth?.user && (
        <div className="md:hidden flex-1 flex justify-center">
          <AllBaiseLogo size={18} />
        </div>
      )}
    </header>
  );
}
